import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Table.css";
import { useVisibleColumns } from "./useResponsiveColumns";

// Safety-net wait. We normally finalize the close on the `animationend`
// event, but if the event never fires (reduced motion, mid-flight unmount,
// browser quirks) this fallback still unmounts the rows.
const GROUP_COLLAPSE_FALLBACK_MS = 500;
const ROW_COLLAPSE_ANIMATION_NAME = "wg-row-collapse";

const Table = ({
  columns,
  data,
  onRowClick,
  hoverable = false,
  emptyState = "No data available",
  className = "",
  rowKey = "id",
  activeRowKey,
  toolbar,
  header,
  renderExpandedRow,
  expandedRowKey,
  isRowExpandable,
  getGroupKey,
  renderGroupHeader,
  groupsCollapsible = false,
  // Optional controlled collapse. When `collapsedGroupKeys` (any iterable of
  // group keys) is given, it decides which groups are collapsed, and the
  // Table reports the user's clicks through
  // `onGroupCollapsedChange(groupKey, collapsed)`: `true` once a group's
  // close animation ends, `false` when a group opens. Without it the Table
  // keeps the collapsed groups itself, which a remount forgets.
  collapsedGroupKeys,
  onGroupCollapsedChange,
  ...rest
}) => {
  const base = "wg-table";

  const [ownCollapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [closingGroups, setClosingGroups] = useState(() => new Set());
  const closeTimers = useRef(new Map());

  const isControlled = collapsedGroupKeys != null;
  const controlledCollapsedGroups = useMemo(
    () => (isControlled ? new Set(collapsedGroupKeys) : null),
    [isControlled, collapsedGroupKeys],
  );
  const collapsedGroups = isControlled
    ? controlledCollapsedGroups
    : ownCollapsedGroups;

  useEffect(() => {
    const timers = closeTimers.current;
    return () => {
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
    };
  }, []);

  const finalizeGroupClose = (groupKey) => {
    // Each closing row's animationend lands here, then maybe the fallback;
    // only the first finds the close still pending.
    const pending = closeTimers.current.has(groupKey);
    const existing = closeTimers.current.get(groupKey);
    if (existing) {
      clearTimeout(existing);
      closeTimers.current.delete(groupKey);
    }
    setClosingGroups((prev) => {
      if (!prev.has(groupKey)) return prev;
      const next = new Set(prev);
      next.delete(groupKey);
      return next;
    });
    if (isControlled) {
      if (pending) onGroupCollapsedChange?.(groupKey, true);
      return;
    }
    setCollapsedGroups((prev) => {
      if (prev.has(groupKey)) return prev;
      const next = new Set(prev);
      next.add(groupKey);
      return next;
    });
  };

  const toggleGroup = (groupKey) => {
    if (collapsedGroups.has(groupKey)) {
      // Open instantly. (Per-row entrance animation would replay on every
      // initial render, which feels worse than a snap-open.)
      if (isControlled) {
        onGroupCollapsedChange?.(groupKey, false);
      } else {
        setCollapsedGroups((prev) => {
          const next = new Set(prev);
          next.delete(groupKey);
          return next;
        });
      }
      const existing = closeTimers.current.get(groupKey);
      if (existing) {
        clearTimeout(existing);
        closeTimers.current.delete(groupKey);
      }
      setClosingGroups((prev) => {
        if (!prev.has(groupKey)) return prev;
        const next = new Set(prev);
        next.delete(groupKey);
        return next;
      });
      return;
    }

    // Closing: keep rows mounted with the `--group-closing` class so the
    // exit animation can play. We finalize the unmount on `animationend`
    // (see row's onAnimationEnd handler) and only fall back to this timer
    // if the event never fires.
    setClosingGroups((prev) => new Set(prev).add(groupKey));
    const timer = setTimeout(() => {
      finalizeGroupClose(groupKey);
    }, GROUP_COLLAPSE_FALLBACK_MS);
    closeTimers.current.set(groupKey, timer);
  };

  const cardClasses = [`${base}__card`, className].filter(Boolean).join(" ");

  const getKey = (row) =>
    typeof rowKey === "function" ? rowKey(row) : row[rowKey];

  const visibleColumns = useVisibleColumns(columns);

  const cellClass = (col) =>
    [
      `${base}__cell`,
      col.align === "center" && `${base}__cell--center`,
      col.align === "right" && `${base}__cell--right`,
      col.wrap && `${base}__cell--wrap`,
    ]
      .filter(Boolean)
      .join(" ");

  const headerCellClass = (col) =>
    [
      `${base}__header-cell`,
      col.align === "center" && `${base}__header-cell--center`,
      col.align === "right" && `${base}__header-cell--right`,
    ]
      .filter(Boolean)
      .join(" ");

  const isObjectToolbar = toolbar && typeof toolbar === "object" && ("left" in toolbar || "right" in toolbar);

  const renderToolbar = () => {
    if (!toolbar) return null;

    if (isObjectToolbar) {
      return (
        <div className={`${base}__toolbar`}>
          <div className={`${base}__toolbar-left`}>{toolbar.left}</div>
          <div className={`${base}__toolbar-right`}>{toolbar.right}</div>
        </div>
      );
    }

    return (
      <div className={`${base}__toolbar`}>
        <div className={`${base}__toolbar-right`}>{toolbar}</div>
      </div>
    );
  };

  return (
    <div {...rest}>
      {renderToolbar()}
      {header}
      <div className={cardClasses}>
        <table className={base}>
          <colgroup>
            {visibleColumns.map((col) => (
              <col
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
              />
            ))}
          </colgroup>
          <thead>
            <tr className={`${base}__header-row`}>
              {visibleColumns.map((col) => (
                <th key={col.key} className={headerCellClass(col)}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>

          {data.length > 0 && (
            <tbody>
              {data.map((row, index) => {
                const key = getKey(row);
                const isActive =
                  activeRowKey != null && String(key) === String(activeRowKey);

                const hasGrouping = typeof getGroupKey === "function";

                const canExpand =
                  typeof renderExpandedRow === "function" &&
                  (typeof isRowExpandable === "function"
                    ? isRowExpandable(row)
                    : true);
                const isExpanded =
                  canExpand &&
                  expandedRowKey != null &&
                  String(key) === String(expandedRowKey);

                const groupKey = hasGrouping ? getGroupKey(row) : null;
                const prevGroupKey =
                  hasGrouping && index > 0 ? getGroupKey(data[index - 1]) : null;
                const showGroupHeader =
                  groupKey != null && groupKey !== prevGroupKey;

                const isGroupCollapsed =
                  groupsCollapsible &&
                  groupKey != null &&
                  collapsedGroups.has(groupKey);
                const isGroupClosing =
                  groupsCollapsible &&
                  groupKey != null &&
                  closingGroups.has(groupKey);

                const rowClasses = [
                  `${base}__row`,
                  hoverable && `${base}__row--hoverable`,
                  isActive && `${base}__row--active`,
                  hasGrouping && `${base}__row--grouped`,
                  isGroupClosing && `${base}__row--group-closing`,
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <React.Fragment key={key}>
                    {showGroupHeader && (
                      <tr
                        className={[
                          `${base}__group-row`,
                          groupsCollapsible && `${base}__group-row--collapsible`,
                          (isGroupCollapsed || isGroupClosing) &&
                            `${base}__group-row--collapsed`,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={
                          groupsCollapsible
                            ? () => toggleGroup(groupKey)
                            : undefined
                        }
                        onKeyDown={
                          groupsCollapsible
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  toggleGroup(groupKey);
                                }
                              }
                            : undefined
                        }
                        role={groupsCollapsible ? "button" : undefined}
                        tabIndex={groupsCollapsible ? 0 : undefined}
                        aria-expanded={
                          groupsCollapsible
                            ? !(isGroupCollapsed || isGroupClosing)
                            : undefined
                        }
                        aria-hidden={groupsCollapsible ? undefined : "true"}
                      >
                        <td colSpan={visibleColumns.length}>
                          {groupsCollapsible && (
                            <span
                              className={`${base}__group-row-chevron`}
                              aria-hidden="true"
                            >
                              <svg
                                viewBox="0 0 12 12"
                                width="10"
                                height="10"
                                focusable="false"
                              >
                                <path
                                  d="M2.5 4.5l3.5 3.5 3.5-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                          )}
                          {typeof renderGroupHeader === "function"
                            ? renderGroupHeader(groupKey, row)
                            : groupKey}
                        </td>
                      </tr>
                    )}
                    {!isGroupCollapsed && (
                      <>
                        <tr
                          className={rowClasses}
                          onClick={
                            onRowClick ? () => onRowClick(row) : undefined
                          }
                          onAnimationEnd={
                            isGroupClosing
                              ? (e) => {
                                  if (
                                    e.animationName ===
                                      ROW_COLLAPSE_ANIMATION_NAME &&
                                    e.currentTarget === e.target.parentNode
                                  ) {
                                    finalizeGroupClose(groupKey);
                                  }
                                }
                              : undefined
                          }
                        >
                          {visibleColumns.map((col) => (
                            <td key={col.key} className={cellClass(col)}>
                              {col.render ? col.render(row) : row[col.key]}
                            </td>
                          ))}
                        </tr>
                        {canExpand && (
                          <tr
                            className={[
                              `${base}__expanded-row`,
                              isExpanded &&
                                !isGroupClosing &&
                                `${base}__expanded-row--open`,
                              hasGrouping && `${base}__expanded-row--grouped`,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            aria-hidden={!isExpanded || isGroupClosing}
                          >
                            <td colSpan={visibleColumns.length}>
                              <div className={`${base}__expanded-grid`}>
                                <div className={`${base}__expanded-inner`}>
                                  {renderExpandedRow(row)}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          )}
        </table>
        {data.length === 0 && (
          <p className={`${base}__empty`}>{emptyState}</p>
        )}
      </div>
    </div>
  );
};

export default Table;
